/** The default six deal stages (#885): the offline fallback and the values of the backend
 *  `sales_default` template. `key` is what `deals.stage` stores. An org's definition may add
 *  and remove open stages, but Closed Won and Closed Lost are always present; label, colour,
 *  order and probability come from the definition. `tests/test_pipeline_definitions.py` and
 *  `lib/__tests__/dealStages.test.js` pin the same table, so drift fails a test on either side. */
export const DEFAULT_DEAL_STAGES = [
    { key: 'Prospect', label: 'Prospect', color: '#3b82f6', order: 0, probability: 10, outcome: 'open' },
    { key: 'Qualified', label: 'Qualified', color: '#78716c', order: 1, probability: 25, outcome: 'open' },
    { key: 'Proposal Sent', label: 'Proposal Sent', color: '#f59e0b', order: 2, probability: 50, outcome: 'open' },
    { key: 'Negotiation', label: 'Negotiation', color: '#8b5cf6', order: 3, probability: 75, outcome: 'open' },
    { key: 'Closed Won', label: 'Closed Won', color: '#10b981', order: 4, probability: 100, outcome: 'won' },
    { key: 'Closed Lost', label: 'Closed Lost', color: '#ef4444', order: 5, probability: 0, outcome: 'lost' },
];

// The backend's unmapped-stage colour (`deal_stage_summary`), distinct from Qualified's default grey.
const UNMAPPED_COLOR = '#a8a29e';

/** The two terminal stages every closed-deal check hard-codes. They are locked in the definition. */
export const isClosedDealStage = (key) => key === 'Closed Won' || key === 'Closed Lost';

/** The definition's stages in order, or the defaults when there is no definition. */
export function dealStages(definition) {
    const stages = definition?.stages;
    const list = Array.isArray(stages) && stages.length > 0 ? stages : DEFAULT_DEAL_STAGES;
    return [...list].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

/** Deals whose stored stage is not a key of the definition (a legacy lowercase webhook value,
 *  or a stage an admin force-removed). The Kanban shows them in an "Unmapped stage" column. */
export function unmappedDeals(definition, deals) {
    const keys = new Set(dealStages(definition).map((s) => s.key));
    return (deals || []).filter((d) => !keys.has(d.stage));
}

/** Display metadata for a stored stage value. An unknown value renders as itself instead of vanishing. */
export function dealStageMeta(definition, key) {
    const stage = dealStages(definition).find((s) => s.key === key);
    if (stage) return stage;
    return { key, label: key ?? '—', color: UNMAPPED_COLOR, probability: null, outcome: 'open' };
}

/** A blank deal form. `stage` is the column "Add deal" was clicked in; without one the deal starts in the definition's default stage. */
export function newDealForm(definition, stage) {
    const key = stage ?? definition?.default_stage ?? dealStages(definition)[0].key;
    return { title: '', account_id: '', account_name: '', stage: key, value: '', probability: dealStageMeta(definition, key).probability, expected_close_date: '', description: '', source: '', tags: [] };
}

/** The write refused a stage the board still offers: the definition changed since this page read it. */
export const isUnknownStage = (err) => err?.response?.data?.detail?.code === 'unknown_deal_stage';
